import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Send, Loader2 } from "lucide-react";
import { API, WHATSAPP_NUMBER, CONTACT_EMAIL } from "@/config";

const businessTypes = [
  "Landing Page",
  "E-commerce",
  "Sito con Prenotazioni",
  "Sito con Menu / Catalogo",
  "Sito Vetrina",
  "Altro",
];

const budgets = ["Meno di 1.000€", "1.000€ – 2.500€", "2.500€ – 5.000€", "Oltre 5.000€", "Non lo so ancora"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-zinc-900/70 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-all duration-300 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", business_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onPrefill = (e) =>
      setForm((f) => ({ ...f, business_type: e.detail.business_type, message: e.detail.message }));
    window.addEventListener("prefill-quote", onPrefill);
    return () => window.removeEventListener("prefill-quote", onPrefill);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Richiesta inviata! Ti ricontatto entro 24 ore.");
      setForm({ name: "", email: "", business_type: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o scrivimi su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Ciao! Ho visto MPX Digital e vorrei un preventivo per il mio sito web."
  )}`;

  return (
    <section id="contatti" className="relative py-24 lg:py-32" data-testid="contact-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/3 h-[26rem] w-[26rem] rounded-full bg-cyan-500/8 blur-[140px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">Contatti</p>
          <h2 className="font-syne text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Parliamo del tuo progetto.{" "}
            <span className="text-zinc-500">Rispondo entro 24 ore.</span>
          </h2>
          <p className="mt-5 max-w-md text-zinc-400 leading-relaxed">
            Compila il modulo oppure scrivimi direttamente: analizzo la tua idea e ti propongo la soluzione migliore, con preventivo gratuito e senza impegno.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-6 py-3.5 font-semibold text-zinc-950 transition-all duration-300 hover:brightness-110 hover:shadow-[0_0_28px_rgba(37,211,102,0.35)]"
              data-testid="whatsapp-button"
            >
              <MessageCircle size={18} />
              Parla subito su WhatsApp
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Richiesta preventivo sito web")}`}
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/15 px-6 py-3.5 font-semibold text-zinc-100 transition-all duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
              data-testid="email-button"
            >
              <Mail size={18} />
              Scrivimi una mail
            </a>
          </div>

          <p className="mt-8 font-mono text-xs text-zinc-500">
            {CONTACT_EMAIL} · Italia / Remote
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md p-8"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                Nome *
              </label>
              <input
                id="contact-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Mario Rossi"
                className={inputCls}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                Email *
              </label>
              <input
                id="contact-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="mario@esempio.it"
                className={inputCls}
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label htmlFor="contact-business" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                Tipo di progetto *
              </label>
              <select
                id="contact-business"
                required
                value={form.business_type}
                onChange={set("business_type")}
                className={`${inputCls} appearance-none`}
                data-testid="contact-business-select"
              >
                <option value="" disabled>
                  Seleziona…
                </option>
                {businessTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-budget" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                Budget indicativo
              </label>
              <select
                id="contact-budget"
                value={form.budget}
                onChange={set("budget")}
                className={`${inputCls} appearance-none`}
                data-testid="contact-budget-select"
              >
                <option value="">Seleziona…</option>
                {budgets.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              Raccontami la tua idea *
            </label>
            <textarea
              id="contact-message"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Ho un barber shop a Torino e vorrei un sito con prenotazioni online…"
              className={`${inputCls} resize-none`}
              data-testid="contact-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-6 py-3.5 font-semibold text-zinc-950 transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(56,189,248,0.4)] disabled:opacity-60"
            data-testid="contact-form-submit-button"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? "Invio in corso…" : "Invia la richiesta"}
          </button>
          <p className="mt-4 text-center text-xs text-zinc-500">
            Nessuno spam, nessun impegno. Inviando il modulo accetti la{" "}
            <a href="/privacy" className="text-cyan-400/80 hover:text-cyan-300 underline underline-offset-2">
              Privacy Policy
            </a>
            .
          </p>
        </motion.form>
      </div>
    </section>
  );
};
